import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { on } from '@cornell-engineering/js/utils';
import { KEY_CODES } from '@cornell-engineering/js/utils/constants';

/**
 * Menu
 *
 * Add behaviors to open, close, and navigate within the mobile/small screen menu
 */
class Menu {
  constructor(classname) {
    this.classname = classname;

    this.body = document.querySelector('body');
    this.logo = document.querySelector('.nav__logo-link');
    this.menu = document.querySelector(`${this.classname}`);
    this.menuToggle = document.querySelector(`${this.classname}-toggle`);
    this.menuItems = document.querySelectorAll(`.menu-item`);
    this.menuIsOpen = false;
    // Set focusable elements
    this.focusableEls = [
      this.menu.querySelector(`${this.classname}-toggle`),
      ...Array.from(this.menu.querySelectorAll('a, select')),
    ];

    this.firstFocusableEl = this.focusableEls[0];
    this.lastFocusableEl = this.focusableEls[this.focusableEls.length - 1];

    this.listeners = [];

    this.bindEvents();
  }

  /**
   * Returns the classname without the starting `.`
   *
   * @example
   * this.classname = '.js--menu';
   * // this.plainClassname === 'js--menu'
   */
  get plainClassname() {
    return this.classname.replace(/^\.*/g, '');
  }

  get activeClass() {
    return `${this.plainClassname}-active`;
  }

  bindEvents() {
    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => this.toggle());
    }

    this.menuItems.forEach(item => {
      if (item.querySelector('.menu__submenu')) {
        item
          .querySelector('.menu-item__expand-btn')
          .addEventListener('click', () => this.toggleSubmenu(item));
      }
    });
  }

  isDesktopMenu() {
    return window.matchMedia('(min-width: 1200px)').matches;
  }

  toggleSubmenu(target) {
    if (target.classList.contains('active')) {
      this.onSubmenuCollapse(target);
    } else {
      this.onSubmenuExpand(target);
    }
  }

  expandInitialSubmenu() {
    if (this.menuItems && this.menuItems.length) {
      const firstSubmenu = this.menuItems[0];
      this.onSubmenuExpand(firstSubmenu);
    }
  }

  collapseActiveSubmenu() {
    const currentActiveEl = document.querySelector('.menu-item.active');
    if (currentActiveEl) {
      this.onSubmenuCollapse(currentActiveEl);
    }
  }

  onSubmenuExpand(target) {
    this.collapseActiveSubmenu();

    target.classList.add('active');
    target.querySelector('.menu-item__expand-btn').setAttribute('aria-expanded', 'true');
  }

  onSubmenuCollapse(target) {
    target.classList.remove('active');
    target.querySelector('.menu-item__expand-btn').setAttribute('aria-expanded', 'false');
  }

  open() {
    this.menuToggle.setAttribute('aria-expanded', true);
    this.menu.setAttribute('aria-hidden', false);
    this.body.classList.add(this.activeClass);

    // Hide sibling elements from screen readers
    Array.from(document.querySelector('.layout--base')?.children).forEach(child => {
      if (child !== this.menu) {
        child.setAttribute('aria-hidden', true);
      }
    });

    this.menuIsOpen = true;

    // Focus on the menu after a delay to
    // account for enter animation
    setTimeout(() => {
      this.menu.focus();
      this.menu.querySelector('.js--menu-toggle')?.addEventListener('click', () => {
        this.close();
      });
    }, 100);

    this.listeners.push(on(document, 'keydown', this.keydown.bind(this)));

    if (this.isDesktopMenu()) {
      this.expandInitialSubmenu();
    }

    disableBodyScroll(this.menu);
  }

  close() {
    this.menuToggle.setAttribute('aria-expanded', false);
    this.menu.setAttribute('aria-hidden', true);
    this.body.classList.remove(this.activeClass);

    // Show sibling elements to screen readers
    Array.from(document.querySelector('.layout--base')?.children).forEach(child => {
      if (child !== this.menu) {
        child.setAttribute('aria-hidden', false);
      }
    });

    this.menuToggle.focus();
    this.menuIsOpen = false;
    this.listeners.forEach(off => {
      off();
    });

    this.collapseActiveSubmenu();

    enableBodyScroll(this.menu);
  }

  toggle() {
    if (this.menuIsOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  handleBackwardTab(e) {
    if (document.activeElement === this.firstFocusableEl) {
      e.preventDefault();
      this.lastFocusableEl.focus();
    }
  }

  handleForwardTab(e) {
    if (document.activeElement === this.lastFocusableEl) {
      e.preventDefault();
      this.firstFocusableEl.focus();
    }
  }

  keydown(e) {
    switch (e.key) {
      case KEY_CODES.ESCAPE:
      case KEY_CODES.ESCAPE_IE11: {
        this.close();
        break;
      }

      case KEY_CODES.TAB: {
        if (this.focusableEls.length === 1) {
          e.preventDefault();
          break;
        }
        if (e.shiftKey) {
          this.handleBackwardTab(e);
        } else {
          this.handleForwardTab(e);
        }
        break;
      }

      default: {
        break;
      }
    }
  }
}

export default Menu;
